// Admin Trust & Safety Command Console
// STRICTLY RESTRICTED TO coryliseno@gmail.com
import type { ConcernReport, Mission, DadProfile, BannedEmail } from '../types';
import {
  ADMIN_EMAIL,
  isAdmin,
  relistEvent,
  removeEvent,
  removeEventAndBanCreator,
  reenableUserAccount,
  banReportedUser,
  banReportingUser,
  unbanEmail
} from '../services/adminService';
import { showToast } from './Toast';

export function renderAdminDashboard(
  reports: ConcernReport[],
  missions: Mission[],
  users: DadProfile[],
  bannedEmails: BannedEmail[]
): string {
  if (!isAdmin()) {
    return `
      <div class="py-20 text-center space-y-4 max-w-md mx-auto">
        <div class="w-16 h-16 rounded-2xl bg-red-950/40 border border-red-500/50 text-red-400 flex items-center justify-center text-3xl mx-auto">
          <i class="fa-solid fa-lock"></i>
        </div>
        <h2 class="text-xl font-black text-slate-100">Restricted Administration Page</h2>
        <p class="text-xs text-slate-400">
          This unlisted dashboard is strictly reserved for <strong>${ADMIN_EMAIL}</strong>.
        </p>
        <button onclick="window.location.href='/'" class="btn-primary px-5 py-2 rounded-xl text-xs font-bold cursor-pointer">
          Return to Pops Culture
        </button>
      </div>
    `;
  }

  const pendingReports = reports.filter(r => r.status === 'pending');
  const resolvedReports = reports.filter(r => r.status !== 'pending');
  const underReviewEvents = missions.filter(m => m.status === 'under_review');
  const suspendedUsers = users.filter(u => u.status === 'suspended' || u.underReview);

  return `
    <div class="space-y-6 animate-fade-in font-sans">
      
      <!-- Top Command Header -->
      <div class="card-workshop p-6 sm:p-7 relative overflow-hidden bg-[#090b12] border-amber-500/40">
        <div class="laser-line-amber absolute top-0 left-0 right-0"></div>
        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 relative z-10">
          <div>
            <div class="flex items-center gap-2 mb-1">
              <span class="px-2.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-transparent text-amber-400 border border-amber-400/60 shadow-[0_0_10px_rgba(255,183,3,0.25)] flex items-center gap-1.5">
                <i class="fa-solid fa-shield-halved"></i> Unlisted Admin Command
              </span>
              <span class="text-xs text-slate-400">Trust & Safety Console</span>
            </div>
            <h1 class="text-xl sm:text-2xl font-black text-slate-100">Pops Culture Moderation & Review</h1>
            <p class="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
              <span>Authenticated as:</span>
              <strong class="text-emerald-400">${ADMIN_EMAIL}</strong>
            </p>
          </div>
          <div class="flex items-center gap-2">
            <a href="/app" class="btn-secondary px-4 py-2 rounded-xl text-xs font-bold flex items-center gap-2">
              <i class="fa-solid fa-arrow-left"></i> Back to Main App
            </a>
          </div>
        </div>

        <!-- Metric KPI Cards -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-5 mt-5 border-t border-white/[0.08] text-xs">
          <div class="p-3.5 rounded-xl bg-transparent border border-amber-500/30">
            <div class="text-2xl font-black text-amber-400">${pendingReports.length}</div>
            <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Pending Reports</div>
          </div>
          <div class="p-3.5 rounded-xl bg-transparent border border-white/[0.08]">
            <div class="text-2xl font-black text-cyan-400">${underReviewEvents.length}</div>
            <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Events Under Review</div>
          </div>
          <div class="p-3.5 rounded-xl bg-transparent border border-white/[0.08]">
            <div class="text-2xl font-black text-pink-400">${suspendedUsers.length}</div>
            <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Suspended Dads</div>
          </div>
          <div class="p-3.5 rounded-xl bg-transparent border border-red-500/30">
            <div class="text-2xl font-black text-red-400">${bannedEmails.length}</div>
            <div class="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Banned Emails</div>
          </div>
        </div>
      </div>

      <!-- Section: Pending Concern Reports Queue -->
      <div class="space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-base font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
            <i class="fa-solid fa-inbox text-amber-400"></i> Active Concerns Queue (${pendingReports.length})
          </h2>
          <span class="text-xs text-slate-500">Requires review and administrative determination</span>
        </div>

        ${pendingReports.length === 0 ? `
          <div class="p-8 text-center bg-[#0a0c13] border border-white/[0.08] rounded-xl space-y-1">
            <i class="fa-solid fa-circle-check text-emerald-400 text-3xl mb-1"></i>
            <div class="font-bold text-slate-200 text-sm">Inbox Zero &bull; All Clear!</div>
            <p class="text-xs text-slate-500">No unresolved concern reports or pending safety investigations.</p>
          </div>
        ` : `
          <div class="space-y-4">
            ${pendingReports.map(rep => {
              const isEvent = rep.type === 'event';
              const targetEvent = isEvent ? missions.find(m => m.id === rep.targetId) : null;
              const targetUser = !isEvent ? users.find(u => u.uid === rep.targetId) : null;
              const reportingUser = users.find(u => u.uid === rep.reporterUid);

              const creatorEmail = targetEvent ? (users.find(u => u.uid === targetEvent.host.userId)?.email || '') : '';

              return `
                <div class="p-5 rounded-xl bg-[#0a0c13] border border-amber-500/40 space-y-4 shadow-[0_0_15px_rgba(255,183,3,0.1)] relative">
                  
                  <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-3 border-b border-white/[0.08]">
                    <div class="flex items-center gap-2.5">
                      <span class="px-2.5 py-0.5 rounded text-[10px] font-black uppercase ${isEvent ? 'bg-cyan-950/60 border border-cyan-400 text-cyan-300' : 'bg-purple-950/60 border border-purple-400 text-purple-300'}">
                        ${rep.type.toUpperCase()} REPORT
                      </span>
                      <h3 class="font-black text-slate-100 text-sm">
                        ${rep.targetNameOrTitle}
                      </h3>
                      <span class="text-[11px] text-slate-500">ID: <code>${rep.targetId}</code></span>
                    </div>
                    <span class="text-xs text-slate-400">
                      Reported ${new Date(rep.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <!-- Concern Details -->
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    
                    <div class="p-3.5 rounded-xl bg-transparent border border-white/[0.08] space-y-2">
                      <div class="flex items-center justify-between">
                        <span class="font-bold text-slate-300">Category Flagged:</span>
                        <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-400/40">
                          ${rep.categoryLabel}
                        </span>
                      </div>
                      <div>
                        <span class="text-slate-400 block mb-1 font-semibold">Reporter Message:</span>
                        <div class="p-2.5 rounded-lg bg-black/40 border border-white/[0.06] text-slate-200 text-xs italic">
                          "${rep.message || 'No additional message provided.'}"
                        </div>
                      </div>
                    </div>

                    <div class="p-3.5 rounded-xl bg-transparent border border-white/[0.08] space-y-2 text-slate-300">
                      <div class="font-bold text-slate-200">Reporting User Information:</div>
                      <div>
                        <span class="text-slate-400">Name:</span> ${reportingUser?.name || 'Unknown'}
                      </div>
                      <div>
                        <span class="text-slate-400">Email:</span> <strong>${rep.reporterEmail}</strong>
                      </div>
                      <div>
                        <span class="text-slate-400">User ID:</span> <code>${rep.reporterUid}</code>
                      </div>
                      ${creatorEmail ? `
                        <div class="pt-1 border-t border-white/[0.06]">
                          <span class="text-slate-400">Event Creator Email:</span> <strong class="text-cyan-300">${creatorEmail}</strong>
                        </div>
                      ` : ''}
                    </div>

                  </div>

                  <!-- Admin Action Decision Buttons -->
                  <div class="pt-3 border-t border-white/[0.08] flex items-center justify-between flex-wrap gap-2 text-xs">
                    <span class="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">
                      Administrative Actions:
                    </span>

                    <div class="flex items-center gap-2 flex-wrap">
                      
                      ${isEvent ? `
                        <!-- Option A: No Violation -> Relist Event -->
                        <button 
                          data-action="relist-event"
                          data-report-id="${rep.id}"
                          data-event-id="${rep.targetId}"
                          class="admin-action-btn px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-950/60 border border-emerald-400 text-emerald-300 hover:bg-emerald-900/60 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <i class="fa-solid fa-check"></i> Relist Event (No Violation)
                        </button>

                        <!-- Option B: Violated Rules -> Remove Event Only -->
                        <button 
                          data-action="remove-event"
                          data-report-id="${rep.id}"
                          data-event-id="${rep.targetId}"
                          class="admin-action-btn px-3 py-1.5 rounded-lg text-xs font-bold bg-amber-950/60 border border-amber-400 text-amber-300 hover:bg-amber-900/60 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <i class="fa-solid fa-trash-can"></i> Remove Event
                        </button>

                        <!-- Option C: Severe -> Remove Event & Ban Creator -->
                        <button 
                          data-action="ban-creator"
                          data-report-id="${rep.id}"
                          data-event-id="${rep.targetId}"
                          data-creator-uid="${targetEvent?.host.userId || ''}"
                          data-creator-email="${creatorEmail}"
                          class="admin-action-btn px-3 py-1.5 rounded-lg text-xs font-bold bg-red-950/60 border border-red-500 text-red-300 hover:bg-red-900/60 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <i class="fa-solid fa-ban"></i> Remove & Ban Creator
                        </button>
                      ` : `
                        <!-- User Report Option A: No Violation -> Re-enable Account -->
                        <button 
                          data-action="reenable-user"
                          data-report-id="${rep.id}"
                          data-target-uid="${rep.targetId}"
                          class="admin-action-btn px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-950/60 border border-emerald-400 text-emerald-300 hover:bg-emerald-900/60 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <i class="fa-solid fa-user-check"></i> Re-enable Account (No Violation)
                        </button>

                        <!-- User Report Option B: Violated Rules -> Ban User Permanently -->
                        <button 
                          data-action="ban-user"
                          data-report-id="${rep.id}"
                          data-target-uid="${rep.targetId}"
                          data-target-email="${targetUser?.email || ''}"
                          class="admin-action-btn px-3 py-1.5 rounded-lg text-xs font-bold bg-red-950/60 border border-red-500 text-red-300 hover:bg-red-900/60 transition-colors flex items-center gap-1 cursor-pointer"
                        >
                          <i class="fa-solid fa-ban"></i> Ban Reported User
                        </button>
                      `}

                      <!-- Option D: Malicious Flagging by Reporter -> Ban Reporter -->
                      <button 
                        data-action="ban-reporter"
                        data-report-id="${rep.id}"
                        data-reporter-uid="${rep.reporterUid}"
                        data-reporter-email="${rep.reporterEmail}"
                        class="admin-action-btn px-3 py-1.5 rounded-lg text-xs font-bold text-slate-400 border border-white/10 hover:text-red-400 hover:border-red-500/50 transition-colors flex items-center gap-1 cursor-pointer"
                        title="Ban reporting user for filing false/malicious reports"
                      >
                        <i class="fa-solid fa-gavel"></i> Ban Reporter (Malicious)
                      </button>

                    </div>
                  </div>

                </div>
              `;
            }).join('')}
          </div>
        `}
      </div>

      <!-- Section: Banned Emails Registry -->
      <div class="pt-6 border-t border-white/[0.08] space-y-4">
        <div class="flex items-center justify-between">
          <h2 class="text-base font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
            <i class="fa-solid fa-ban text-red-400"></i> Banned Emails Registry (${bannedEmails.length})
          </h2>
          <span class="text-xs text-slate-500">Users with these emails cannot sign in or access Pops Culture</span>
        </div>

        ${bannedEmails.length === 0 ? `
          <div class="p-6 text-center bg-[#0a0c13] border border-white/[0.08] rounded-xl text-xs text-slate-500">
            No banned emails in the registry.
          </div>
        ` : `
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            ${bannedEmails.map(b => `
              <div class="p-3.5 rounded-xl bg-red-950/20 border border-red-500/30 flex items-center justify-between gap-3 text-xs">
                <div class="min-w-0">
                  <div class="font-bold text-slate-200 truncate">${b.email}</div>
                  <div class="text-[10px] text-slate-400 truncate">${b.reason || 'Rule violation'} &bull; ${new Date(b.bannedAt).toLocaleDateString()}</div>
                </div>
                <button 
                  data-unban-email="${b.email}" 
                  class="unban-email-btn text-xs text-slate-400 hover:text-emerald-400 p-1.5 transition-colors cursor-pointer"
                  title="Lift ban on this email"
                >
                  <i class="fa-solid fa-unlock"></i>
                </button>
              </div>
            `).join('')}
          </div>
        `}
      </div>

      <!-- Section: Registered Members & Internal Profile Reporting Tool -->
      <div class="pt-6 border-t border-white/[0.08] space-y-4">
        <div class="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 class="text-base font-bold text-slate-200 uppercase tracking-wider flex items-center gap-2">
              <i class="fa-solid fa-users-gear text-cyan-400"></i> Registered Member Directory & Internal Reporting Tool (${users.length})
            </h2>
            <p class="text-xs text-slate-400 mt-0.5">
              Internal administrative reporting: Displays full profile information (Google OAuth credentials, private bios, neighborhoods, and family squads).
            </p>
          </div>
          <span class="text-[10px] px-2.5 py-1 rounded bg-cyan-950/60 border border-cyan-400/40 text-cyan-300 font-mono">
            Admin Eyes Only
          </span>
        </div>

        ${users.length === 0 ? `
          <div class="p-6 text-center bg-[#0a0c13] border border-white/[0.08] rounded-xl text-xs text-slate-500">
            No registered users in database yet.
          </div>
        ` : `
          <div class="space-y-3">
            ${users.map(u => `
              <div class="p-4 rounded-xl bg-[#0a0c13] border border-white/[0.08] hover:border-cyan-400/30 transition-colors space-y-3 text-xs">
                
                <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pb-2.5 border-b border-white/[0.06]">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-transparent border border-cyan-400/40 text-cyan-400 font-bold flex items-center justify-center text-sm">
                      ${u.avatarInitials}
                    </div>
                    <div>
                      <div class="flex items-center gap-2">
                        <span class="font-black text-slate-100 text-sm">${u.name}</span>
                        ${u.displayName ? `
                          <span class="px-2 py-0.5 rounded text-[10px] bg-cyan-950/80 border border-cyan-400/50 text-cyan-300 font-bold">
                            Display: "${u.displayName}"
                          </span>
                        ` : `
                          <span class="text-[10px] text-slate-500 italic">No custom display name</span>
                        `}
                        <span class="px-2 py-0.5 rounded text-[10px] font-bold ${u.status === 'suspended' ? 'bg-amber-950 text-amber-300 border border-amber-500/40' : u.status === 'banned' ? 'bg-red-950 text-red-300 border border-red-500/40' : 'bg-emerald-950 text-emerald-300 border border-emerald-500/40'}">
                          ${u.status.toUpperCase()}
                        </span>
                      </div>
                      <div class="text-[11px] text-slate-400 flex items-center gap-2 mt-0.5">
                        <span class="text-cyan-300 font-mono"><i class="fa-solid fa-envelope text-[10px]"></i> ${u.email}</span>
                        <span>&bull;</span>
                        <span>UID: <code class="text-[10px] text-slate-500">${u.uid}</code></span>
                      </div>
                    </div>
                  </div>
                  <div class="text-[11px] text-slate-400 self-start sm:self-center">
                    Joined: ${new Date(u.createdAt).toLocaleDateString()} &bull; Last Login: ${new Date(u.lastLoginAt).toLocaleDateString()}
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-[11px] text-slate-300">
                  <div class="p-2.5 rounded-lg bg-[#06070a] border border-white/[0.04]">
                    <span class="text-slate-500 block text-[10px] uppercase font-bold">Neighborhood Base</span>
                    <span class="text-slate-200 font-semibold">${u.neighborhood || 'Not specified'}</span>
                  </div>
                  <div class="p-2.5 rounded-lg bg-[#06070a] border border-white/[0.04]">
                    <span class="text-slate-500 block text-[10px] uppercase font-bold">Years as Dad</span>
                    <span class="text-slate-200 font-semibold">${u.yearsAsDad || 0} years</span>
                  </div>
                  <div class="p-2.5 rounded-lg bg-[#06070a] border border-white/[0.04]">
                    <span class="text-slate-500 block text-[10px] uppercase font-bold">Favorite Beverage</span>
                    <span class="text-pink-300 font-semibold truncate block">${u.beverage || 'None'}</span>
                  </div>
                  <div class="p-2.5 rounded-lg bg-[#06070a] border border-white/[0.04]">
                    <span class="text-slate-500 block text-[10px] uppercase font-bold">Family Squad / Kids</span>
                    <span class="text-slate-200 font-semibold">${u.kids?.length || 0} listed</span>
                  </div>
                </div>

                ${u.bio ? `
                  <div class="text-[11px] text-slate-300 bg-[#06070a] p-2.5 rounded-lg border border-white/[0.04]">
                    <span class="text-slate-500 font-bold block text-[10px] uppercase">Private Dad Bio:</span>
                    ${u.bio}
                  </div>
                ` : ''}

                ${u.skills && u.skills.length > 0 ? `
                  <div class="flex items-center gap-1.5 flex-wrap text-[11px]">
                    <span class="text-slate-500 text-[10px] uppercase font-bold mr-1">Skills:</span>
                    ${u.skills.map(s => `
                      <span class="px-2 py-0.5 rounded bg-white/[0.04] text-slate-300 border border-white/[0.06] text-[10px]">
                        ${s}
                      </span>
                    `).join('')}
                  </div>
                ` : ''}

              </div>
            `).join('')}
          </div>
        `}
      </div>

      <!-- Section: Resolved Reports History -->
      ${resolvedReports.length > 0 ? `
        <div class="pt-6 border-t border-white/[0.08] space-y-3 text-xs">
          <h3 class="font-bold text-slate-400 uppercase tracking-wider">
            Past Resolved Reports (${resolvedReports.length})
          </h3>
          <div class="space-y-2 max-h-56 overflow-y-auto">
            ${resolvedReports.map(r => `
              <div class="p-2.5 rounded-lg bg-transparent border border-white/[0.06] flex items-center justify-between text-slate-400 text-[11px]">
                <div class="flex items-center gap-2">
                  <span class="font-bold text-slate-300">${r.type.toUpperCase()}: ${r.targetNameOrTitle}</span>
                  <span>&bull;</span>
                  <span class="${r.status === 'resolved_violation' ? 'text-red-400' : 'text-emerald-400'} font-semibold">${r.status}</span>
                </div>
                <span class="text-slate-500">${r.adminNotes || ''}</span>
              </div>
            `).join('')}
          </div>
        </div>
      ` : ''}

    </div>
  `;
}

export function bindAdminDashboardEvents(onRefresh: () => void): void {
  // Handle action buttons
  document.querySelectorAll('.admin-action-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const el = btn as HTMLElement;
      const action = el.dataset.action;
      const reportId = el.dataset.reportId || '';

      try {
        if (action === 'relist-event') {
          const eventId = el.dataset.eventId || '';
          if (confirm('Relist this event? This indicates no rule violation occurred.')) {
            await relistEvent(reportId, eventId);
            showToast('Event cleared and relisted successfully.', 'success');
            onRefresh();
          }
        } else if (action === 'remove-event') {
          const eventId = el.dataset.eventId || '';
          if (confirm('Permanently remove this event for rule violation?')) {
            await removeEvent(reportId, eventId);
            showToast('Event removed.', 'info');
            onRefresh();
          }
        } else if (action === 'ban-creator') {
          const eventId = el.dataset.eventId || '';
          const creatorUid = el.dataset.creatorUid || '';
          const creatorEmail = el.dataset.creatorEmail || '';
          if (confirm(`Permanently remove event AND ban creator (${creatorEmail})?`)) {
            await removeEventAndBanCreator(reportId, eventId, creatorUid, creatorEmail);
            showToast(`Event removed and ${creatorEmail} banned from Pops Culture.`, 'success');
            onRefresh();
          }
        } else if (action === 'reenable-user') {
          const targetUid = el.dataset.targetUid || '';
          if (confirm('Re-enable this user account? This indicates no violation occurred.')) {
            await reenableUserAccount(reportId, targetUid);
            showToast('User account re-enabled.', 'success');
            onRefresh();
          }
        } else if (action === 'ban-user') {
          const targetUid = el.dataset.targetUid || '';
          const targetEmail = el.dataset.targetEmail || '';
          if (confirm(`Permanently ban user (${targetEmail}) from the platform?`)) {
            await banReportedUser(reportId, targetUid, targetEmail);
            showToast(`User ${targetEmail} banned permanently.`, 'success');
            onRefresh();
          }
        } else if (action === 'ban-reporter') {
          const reporterUid = el.dataset.reporterUid || '';
          const reporterEmail = el.dataset.reporterEmail || '';
          if (confirm(`Ban the reporting user (${reporterEmail}) for fraudulent / malicious flagging?`)) {
            await banReportingUser(reportId, reporterUid, reporterEmail);
            showToast(`Malicious reporter ${reporterEmail} banned.`, 'info');
            onRefresh();
          }
        }
      } catch (err: any) {
        showToast(err.message || 'Admin action failed', 'error');
      }
    });
  });

  // Handle unban email
  document.querySelectorAll('.unban-email-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      const email = (btn as HTMLElement).dataset.unbanEmail;
      if (email && confirm(`Lift ban for ${email}?`)) {
        try {
          await unbanEmail(email);
          showToast(`Ban removed for ${email}.`, 'success');
          onRefresh();
        } catch (err: any) {
          showToast(err.message || 'Failed to unban email', 'error');
        }
      }
    });
  });
}
